import { Uuid } from "@/domains/global/identifiers";
import { PusherDomainPrefix, PusherServicePrefix } from "@/domains/pusher/constants";
import { objectModule } from "@/modules/object";
import { validationModule } from "@/modules/validation";
import { AppStoreSyncStore } from "@/store/sync/AppStoreSyncStore";
import { SerializedSyncOperation, SyncOperationGeneric } from "@/store/sync/operations/types";
import { liveQuery, Subscription } from "dexie";
import { Duration } from "luxon";
import { reaction } from "mobx";
import { JsonObject } from "type-fest";

export function generateSyncActionSpaceScopedPusherChannelKey({ spaceId }: { spaceId: Uuid }) {
  const uppercaseSpaceId = spaceId.toUpperCase();
  return `${PusherServicePrefix.BACKEND_API}@${PusherDomainPrefix.SPACE_SCOPED_SYNC_UPDATE}@${uppercaseSpaceId}`;
}

export function generateSyncActionCollectionScopedPusherChannelKey({
  collectionId,
}: {
  collectionId: Uuid;
}) {
  const uppercaseCollectionId = collectionId.toUpperCase();
  return `${PusherServicePrefix.BACKEND_API}@${PusherDomainPrefix.COLLECTION_SCOPED_SYNC_UPDATE}@${uppercaseCollectionId}`;
}

export function generateSyncActionSpaceAccountScopedPusherChannelKey({
  spaceAccountId,
}: {
  spaceAccountId: Uuid;
}) {
  const uppercaseSpaceAccountId = spaceAccountId.toUpperCase();
  return `${PusherServicePrefix.BACKEND_API}@${PusherDomainPrefix.SPACE_ACCOUNT_SCOPED_SYNC_UPDATE}@${uppercaseSpaceAccountId}`;
}

export function generateSyncActionChatConversationScopedPusherChannelKey({
  chatConversationId,
}: {
  chatConversationId: Uuid;
}) {
  const uppercaseChatConversationId = chatConversationId.toUpperCase();
  return `${PusherServicePrefix.BACKEND_API}@${PusherDomainPrefix.CHAT_CONVERSATION_SCOPED_SYNC_UPDATE}@${uppercaseChatConversationId}`;
}

export function generateChatConversationLiveSyncUpdatePusherChannelKey({
  chatConversationId,
}: {
  chatConversationId: string;
}) {
  const uppercaseChatConversationId = chatConversationId.toUpperCase();
  return `${PusherServicePrefix.AUTHENTICATED_BACKEND_API}@${PusherDomainPrefix.CHAT_CONVERSATION_LIVE_SYNC_UPDATE}@${uppercaseChatConversationId}`;
}

export function generateSpaceAccountNoteLiveSyncUpdatePusherChannelKey({
  spaceAccountNoteId,
}: {
  spaceAccountNoteId: string;
}) {
  const uppercaseSpaceAccountNoteId = spaceAccountNoteId.toUpperCase();
  return `${PusherServicePrefix.AUTHENTICATED_BACKEND_API}@${PusherDomainPrefix.SPACE_ACCOUNT_NOTE_LIVE_SYNC_UPDATE}@${uppercaseSpaceAccountNoteId}`;
}

export function generateSessionLiveSyncUpdatePusherChannelKey({
  sessionId,
}: {
  sessionId: string;
}) {
  const uppercaseSessionId = sessionId.toUpperCase();
  return `${PusherServicePrefix.AUTHENTICATED_BACKEND_API}@${PusherDomainPrefix.SESSION_LIVE_SYNC_UPDATE}@${uppercaseSessionId}`;
}

export function standardizeSyncId(syncId: string | undefined | null) {
  if (!syncId) {
    return undefined;
  }

  /**
   * These are our modern "CVR-style" sync IDs.
   * We shouldn't be doing any "standardization" of them.
   */
  if (syncId.startsWith("SYNC__")) {
    return syncId;
  }

  /**
   * Legacy logic for parsing out sync event ids from pre-CVR sync updates.
   *
   * TODO: @MacroMackie follow up with this on Monday, Nov 11.
   */
  const [prefix, uuid] = syncId.split("::");
  if (prefix !== "SYNC") throw new Error(`Invalid sync id: ${syncId}`);
  if (!validationModule.isUuid(uuid)) throw new Error(`Invalid sync id: ${syncId}`);
  return `${prefix}::${uuid.toUpperCase()}`;
}

export function getLoggableOperation(operation?: SyncOperationGeneric) {
  const { ...o } = { ...operation } as unknown as Record<string, unknown>;
  delete o.store;
  o.operationKind = operation?.operationKind || "<missing>";
  return objectModule.safeAsJson(o);
}

export function formatSyncOperation(
  syncOperation?: SyncOperationGeneric | SerializedSyncOperation
): JsonObject | null {
  if (!syncOperation) return null;

  return {
    operation_id: `${syncOperation.operationId}`,
    operation_kind: `${syncOperation.operationKind}`,
    payload: syncOperation.payload as JsonObject,
  };
}

export function saveJsonToFile(obj: object, filename: string) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

const DEFAULT_EXTRA_WAIT = Duration.fromObject({ milliseconds: 250 });

/**
 * @param syncStore - The sync store responsible for managing live query polling.
 * @param query - A function that checks for an expected update from Dexie.
 * @param timeout - How long to wait for the sync/query, as a Duration.
 * @param extraWait - Additional buffer after the query succeeds, as a Duration.
 * @returns A promise that resolves when the query succeeds after the next sync completes.
 */
export const waitForLiveQuerySync = ({
  syncStore,
  query,
  timeout,
  extraWait = DEFAULT_EXTRA_WAIT,
}: {
  syncStore: AppStoreSyncStore;
  query: () => boolean | Promise<boolean>;
  timeout: Duration;
  extraWait?: Duration;
}) => {
  const initialLastSyncedAt = syncStore.lastSyncedAt?.toMillis() ?? 0;
  const timeoutMs = timeout.as("milliseconds");
  const extraWaitMs = extraWait.as("milliseconds");

  return new Promise<void>((resolve, reject) => {
    let hasSettled = false;
    let queryResolved = false;
    let syncResolved = false;

    let subscription: Subscription | null = null;
    let syncDisposer: (() => void) | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (subscription) {
        subscription.unsubscribe();
        subscription = null;
      }
      if (syncDisposer) {
        syncDisposer();
        syncDisposer = null;
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const settle = (cb: () => void) => {
      if (hasSettled) return;
      hasSettled = true;
      cleanup();
      cb();
    };

    const completeIfReady = () => {
      if (queryResolved && syncResolved) {
        settle(() => setTimeout(() => resolve(void 0), extraWaitMs));
      }
    };

    try {
      syncStore.syncQuery.forceRefetch();
    } catch (error) {
      reject(error instanceof Error ? error : new Error("Failed to refetch sync"));
      return;
    }

    subscription = liveQuery(query).subscribe({
      next: result => {
        if (result) {
          queryResolved = true;
          subscription?.unsubscribe();
          completeIfReady();
        }
      },
      error: error => {
        settle(() => reject(error));
      },
      complete: () => {
        settle(() => reject(new Error("Live query completed without returning a result")));
      },
    });

    syncDisposer = reaction(
      () => syncStore.lastSyncedAt?.toMillis() ?? 0,
      lastSyncedAtMillis => {
        if (!syncResolved && lastSyncedAtMillis > initialLastSyncedAt) {
          syncResolved = true;
          completeIfReady();
        }
      },
      { fireImmediately: true }
    );

    timeoutId = setTimeout(() => {
      settle(() => reject(new Error("Live query timed out")));
    }, timeoutMs);
  });
};
